/**
 * Thin client for typecrm BFF endpoints powering the chat surface.
 *
 * Endpoints (phase 1):
 *   GET  /api/{workspaceId}/ai/prompts
 *   GET  /api/{workspaceId}/ai/prompts/{promptType}
 *   POST /api/{workspaceId}/ai/prompts/{promptType}/execute
 *
 * The browser MUST NEVER call typecrm-ai directly — only typecrm
 * (`AIContentResource`) is allowed.
 */

import { getApiBaseUrl, getAuthHeaders } from "../../api/api";
import { get, post } from "../../api/api";
import { createSseStreamParser, type ParsedSseEvent } from "./chatTurnUtils";
import {
  enrichPromptCatalog,
  CHAT_PROMPT_CUSTOM_TYPE,
} from "../constants/chatConstants";
import { buildQuillDeltaFromMarkdown } from "../utils/buildQuillDelta";
import type {
  CallRecordingSummary,
  ChatTurnApproveRequest,
  ChatTurnRequest,
  ExecuteManagingPromptBody,
  ExecuteManagingPromptRawResponse,
  ExecuteManagingPromptResult,
  ManagingPromptAction,
  ManagingPromptCatalogEntry,
  ManagingPromptCategory,
  ManagingPromptDetail,
} from "../types/chat.types";

/** Execution can exceed 60s for research-heavy prompts. */
const EXECUTE_TIMEOUT_MS = 120_000;

interface RawCatalogEntry {
  "prompt-type"?: string;
  promptType?: string;
  name?: string;
  description?: string;
  categories?: ManagingPromptCategory[];
  guidance?: string;
  recordTypes?: ("COMPANY" | "CONTACT" | "SALE")[];
  requiresRecord?: boolean;
  chatEnabled?: boolean;
  contextRequirements?: string[];
  gateUntilEnriched?: boolean;
  runnable?: boolean;
  unavailableReason?: string;
}

function pickPromptType(raw: RawCatalogEntry): string {
  return raw.promptType ?? raw["prompt-type"] ?? "";
}

function toCatalogEntry(raw: RawCatalogEntry): ManagingPromptCatalogEntry {
  return {
    promptType: pickPromptType(raw),
    name: raw.name ?? "",
    description: raw.description ?? "",
    categories: raw.categories ?? [],
    recordTypes: raw.recordTypes,
    requiresRecord: raw.requiresRecord,
    chatEnabled: raw.chatEnabled,
    contextRequirements: raw.contextRequirements,
    gateUntilEnriched: raw.gateUntilEnriched,
    runnable: raw.runnable,
    unavailableReason: raw.unavailableReason,
  };
}

function toDetail(raw: RawCatalogEntry): ManagingPromptDetail {
  return {
    ...toCatalogEntry(raw),
    guidance: raw.guidance ?? "",
  };
}

/**
 * Fetch the managing prompt catalog and strip `guidance` for the lightweight
 * gallery view. Synthesizes the `custom` entry and applies manifest overrides.
 */
export async function listManagingPrompts(
  workspaceId: string,
  opts?: { recordId?: string | null; activityId?: string | null }
): Promise<ManagingPromptCatalogEntry[]> {
  const params: Record<string, string> = {};
  if (opts?.recordId) params.recordId = opts.recordId;
  if (opts?.activityId) params.activityId = opts.activityId;
  const { data } = await get({
    path: `/${workspaceId}/ai/prompts`,
    params: Object.keys(params).length ? params : undefined,
  });
  const entries = Array.isArray(data) ? (data as RawCatalogEntry[]) : [];
  const catalog = entries
    .map(toCatalogEntry)
    .filter((e) => e.promptType && e.promptType !== CHAT_PROMPT_CUSTOM_TYPE);
  return enrichPromptCatalog(catalog);
}

/** Fetch the full detail (with guidance) for a single prompt. */
export async function getManagingPrompt(
  workspaceId: string,
  promptType: string
): Promise<ManagingPromptDetail | null> {
  const { data } = await get({
    path: `/${workspaceId}/ai/prompts/${encodeURIComponent(promptType)}`,
  });
  if (!data) return null;
  return toDetail(data as RawCatalogEntry);
}

/** Lists call/meeting activities linked to a CRM record (for transcript picker). */
export async function listCallRecordings(
  workspaceId: string,
  recordId: string,
  limit = 20
): Promise<CallRecordingSummary[]> {
  const { data } = await get({
    path: `/${workspaceId}/ai/records/${encodeURIComponent(recordId)}/call-recordings`,
    params: { limit },
  });
  return Array.isArray(data) ? (data as CallRecordingSummary[]) : [];
}

/**
 * One-shot execution of a managing prompt against a CRM record.
 * Normalises the snake_case metadata into the camelCase shape the UI consumes.
 */
export async function executeManagingPrompt(
  workspaceId: string,
  promptType: string,
  body: ExecuteManagingPromptBody
): Promise<ExecuteManagingPromptResult> {
  const payload: ExecuteManagingPromptBody = {
    ...body,
    language: body.language ?? "en",
  };

  const { data } = await post({
    path: `/${workspaceId}/ai/prompts/${encodeURIComponent(promptType)}/execute`,
    data: payload,
    timeoutMs: EXECUTE_TIMEOUT_MS,
  });

  return normaliseExecuteResponse(data as ExecuteManagingPromptRawResponse, promptType);
}

const MANAGING_PROMPT_ACTION_TYPES = new Set([
  "send-email",
  "create-task",
  "create-note",
  "schedule-meeting",
  "schedule-call",
]);

function normaliseActions(raw: ExecuteManagingPromptRawResponse): ManagingPromptAction[] {
  const fromRoot = raw?.actions;
  const fromStructured = raw?.structured?.actions;
  const list = (fromRoot?.length ? fromRoot : fromStructured) ?? [];
  return list.filter(
    (a): a is ManagingPromptAction =>
      Boolean(a?.type && a?.label && MANAGING_PROMPT_ACTION_TYPES.has(a.type))
  );
}

export function normaliseExecuteResponse(
  raw: ExecuteManagingPromptRawResponse,
  fallbackPromptType: string
): ExecuteManagingPromptResult {
  const meta = raw?.metadata ?? {};
  const actions = normaliseActions(raw);
  const summary = raw?.value ?? raw?.structured?.summary ?? "";
  return {
    promptType: raw?.promptType ?? raw?.["prompt-type"] ?? fallbackPromptType,
    value: summary,
    actions,
    contentDelta:
      raw?.contentDelta ??
      (summary ? buildQuillDeltaFromMarkdown(summary, actions) : undefined),
    structured:
      raw?.structured ??
      (summary || actions.length
        ? { summary, actions }
        : undefined),
    metadata: {
      cached: meta.cached,
      modelId: meta.model,
      responseTimeMs:
        typeof meta.response_time === "number" ? meta.response_time * 1000 : undefined,
      estimatedCostUsd: meta.estimated_cost_usd,
      tokenUsage: meta.token_usage
        ? {
            prompt: meta.token_usage.prompt_tokens ?? 0,
            completion: meta.token_usage.completion_tokens ?? 0,
            total: meta.token_usage.total_tokens ?? 0,
          }
        : undefined,
      researchSources: meta.research_sources,
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Phase 2 — agent loop SSE (`POST /api/{w}/ai/chat/turn`)                    */
/* -------------------------------------------------------------------------- */

function chatTurnPath(workspaceId: string, turnId?: string): string {
  const base = `/${workspaceId}/ai/chat/turn`;
  return turnId ? `${base}/${encodeURIComponent(turnId)}` : base;
}

/** Same auth headers as axios `get`/`post` — required for `@ContextJwt` on the BFF. */
async function chatTurnHeaders(workspaceId: string): Promise<HeadersInit> {
  return {
    ...(await getAuthHeaders()),
    "X-Workspace-Id": workspaceId,
    Accept: "text/event-stream",
    "Content-Type": "application/json",
  };
}

export interface PostChatTurnOptions {
  workspaceId: string;
  body: ChatTurnRequest;
  onEvent: (event: ParsedSseEvent) => void;
  signal?: AbortSignal;
}

/**
 * Streams SSE from `POST /api/{workspaceId}/ai/chat/turn` (fetch — not axios).
 */
export async function postChatTurn({
  workspaceId,
  body,
  onEvent,
  signal,
}: PostChatTurnOptions): Promise<void> {
  const url = `${getApiBaseUrl()}${chatTurnPath(workspaceId)}`;
  const response = await fetch(url, {
    method: "POST",
    headers: await chatTurnHeaders(workspaceId),
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    let message = `Chat turn failed (${response.status})`;
    try {
      const errBody = (await response.json()) as { error?: string; message?: string };
      message = errBody.message ?? errBody.error ?? message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  if (!response.body) {
    throw new Error("Chat turn response has no body");
  }

  const parser = createSseStreamParser(onEvent);
  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.feed(decoder.decode(value, { stream: true }));
  }
  parser.flush();
}

export async function deleteChatTurn(workspaceId: string, turnId: string): Promise<void> {
  const url = `${getApiBaseUrl()}${chatTurnPath(workspaceId, turnId)}`;
  const response = await fetch(url, {
    method: "DELETE",
    headers: await chatTurnHeaders(workspaceId),
  });
  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to abort chat turn (${response.status})`);
  }
}

export async function postChatTurnApprove(
  workspaceId: string,
  turnId: string,
  body: ChatTurnApproveRequest
): Promise<void> {
  const url = `${getApiBaseUrl()}${chatTurnPath(workspaceId, turnId)}/approve`;
  const headers = await chatTurnHeaders(workspaceId);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      ...headers,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (!response.ok) {
    let message = `Approve failed (${response.status})`;
    try {
      const errBody = (await response.json()) as { error?: string; message?: string };
      message = errBody.message ?? errBody.error ?? message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
}
